import { useEffect, useRef, type RefObject } from 'react';
import type { HeatmapScrollOptions, HeatmapTableHandle } from '../components/HeatmapTable';
import type { AutoCenterMode } from '../lib/autoCenterPreference';
import { useAutoCenterPreference } from './useAutoCenterPreference';
import { FIT_LAYOUT_MEDIA_QUERY, useMediaQuery } from './useMediaQuery';

interface UseAutoCenterControllerArgs {
  activeViewKey: string;
  hasHeatmapData: boolean;
  heatmapRef: RefObject<HeatmapTableHandle | null>;
}

interface UseAutoCenterControllerResult {
  autoCenterMode: AutoCenterMode;
  autoCenterDisabled: boolean;
  onCenterSpot: () => void;
  onCenterKing: () => void;
}

export function useAutoCenterController({
  activeViewKey,
  hasHeatmapData,
  heatmapRef,
}: UseAutoCenterControllerArgs): UseAutoCenterControllerResult {
  const { autoCenterMode, autoCenterPreferenceReady, updateAutoCenterMode } =
    useAutoCenterPreference();
  const lastAutoCenteredTargetRef = useRef<string | null>(null);
  // Automatic centering only scrolls in the fit layout, so crossing the breakpoint is a new target.
  const fitsLayout = useMediaQuery(FIT_LAYOUT_MEDIA_QUERY);

  useEffect(() => {
    if (!autoCenterPreferenceReady || autoCenterMode === 'off' || !hasHeatmapData) {
      return;
    }

    const nextTargetKey = `${activeViewKey}:${autoCenterMode}:${fitsLayout}`;
    if (lastAutoCenteredTargetRef.current === nextTargetKey) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      scrollHeatmapToMode(heatmapRef, autoCenterMode, { automatic: true });
      lastAutoCenteredTargetRef.current = nextTargetKey;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [
    activeViewKey,
    autoCenterMode,
    autoCenterPreferenceReady,
    fitsLayout,
    hasHeatmapData,
    heatmapRef,
  ]);

  function selectAutoCenterMode(nextMode: AutoCenterMode) {
    updateAutoCenterMode(nextMode);
    if (nextMode === 'off') {
      lastAutoCenteredTargetRef.current = null;
    } else {
      lastAutoCenteredTargetRef.current = `${activeViewKey}:${nextMode}:${fitsLayout}`;
      scrollHeatmapToMode(heatmapRef, nextMode);
    }
  }

  return {
    autoCenterMode,
    autoCenterDisabled: !autoCenterPreferenceReady || !hasHeatmapData,
    onCenterSpot: () => {
      const nextMode: AutoCenterMode = autoCenterMode === 'spot' ? 'off' : 'spot';
      selectAutoCenterMode(nextMode);
    },
    onCenterKing: () => {
      const nextMode: AutoCenterMode = autoCenterMode === 'king' ? 'off' : 'king';
      selectAutoCenterMode(nextMode);
    },
  };
}

function scrollHeatmapToMode(
  heatmapRef: RefObject<HeatmapTableHandle | null>,
  mode: Exclude<AutoCenterMode, 'off'>,
  options?: HeatmapScrollOptions,
) {
  if (mode === 'spot') {
    heatmapRef.current?.scrollToSpot(options);
    return;
  }

  heatmapRef.current?.scrollToKing(options);
}
